/**
 * The AudioWorklet half: cuts the microphone into 480 sample hops for the
 * worker and plays back what it returns. Two hops are buffered before
 * playing (20 ms) to absorb the worker's timing; if it falls behind, old hops
 * are dropped so the delay can't grow. Silent hops (muted mic) are sent as 0
 * so the worker skips the model. If the worker stops answering for 300 ms the
 * page is told ("stalled") so it can switch engines.
 * Konkord: the same when the voice breaks up: three gaps (the worker late
 * again and again, e.g. the app minimized while a game takes the CPU) within
 * 2 s hand over to an engine that runs on the audio thread itself.
 */
export const PROCESSOR = "konkord-dpdfnet";
export const WORKLET_CODE = `
const HOP = 480;
const STALL_QUANTA = 112; // ~300 ms of 128 sample blocks
const CHOPPY_GAPS = 3;
const CHOPPY_QUANTA = 750; // ~2 s of 128 sample blocks
class KonkordDpdfnet extends AudioWorkletProcessor {
  constructor() {
    super();
    this.hop = new Float32Array(HOP);
    this.filled = 0;
    this.loud = false;
    this.queue = [];
    this.current = null;
    this.read = 0;
    this.playing = false;
    this.worker = null;
    this.waiting = 0;
    this.sinceAnswer = 0;
    this.stalled = false;
    this.stopped = false;
    this.blocks = 0;
    this.gaps = [];
    this.port.onmessage = (event) => {
      const data = event.data || {};
      if (data.port) {
        this.worker = data.port;
        this.worker.onmessage = (message) => {
          this.waiting--;
          this.sinceAnswer = 0;
          this.queue.push(message.data);
        };
      } else if (data.stop) {
        this.stopped = true;
        if (this.worker) this.worker.close();
      }
    };
  }
  process(inputs, outputs) {
    if (this.stopped) return false;
    this.blocks++;
    const input = inputs[0] && inputs[0][0];
    const output = outputs[0];
    if (!output || !output[0]) return true;
    if (input && this.worker) {
      for (let i = 0; i < input.length; i++) {
        const v = input[i];
        if (v > 1e-6 || v < -1e-6) this.loud = true;
        this.hop[this.filled++] = v;
        if (this.filled === HOP) {
          if (this.loud) {
            this.worker.postMessage(this.hop, [this.hop.buffer]);
            this.hop = new Float32Array(HOP);
          } else {
            this.worker.postMessage(0);
          }
          this.waiting++;
          this.filled = 0;
          this.loud = false;
        }
      }
    }
    // the worker stopped answering: tell the page once
    if (this.waiting > 0 && ++this.sinceAnswer > STALL_QUANTA && !this.stalled) {
      this.stalled = true;
      this.port.postMessage({ stalled: true });
    }
    // the worker fell behind: drop old audio rather than drift further back
    while (this.queue.length > 6) this.queue.shift();
    if (!this.playing && this.queue.length >= 2) this.playing = true;
    const out = output[0];
    for (let i = 0; i < out.length; i++) {
      if (!this.playing) { out[i] = 0; continue; }
      if (!this.current || this.read >= HOP) {
        this.current = this.queue.shift() || null;
        this.read = 0;
        if (!this.current) {
          this.playing = false;
          out[i] = 0;
          // the voice broke up: too often and the page switches engines
          this.gaps.push(this.blocks);
          while (this.gaps[0] < this.blocks - CHOPPY_QUANTA) this.gaps.shift();
          if (this.gaps.length >= CHOPPY_GAPS && !this.stalled) {
            this.stalled = true;
            this.port.postMessage({ stalled: true, choppy: true });
          }
          continue;
        }
      }
      out[i] = this.current[this.read++];
    }
    for (let c = 1; c < output.length; c++) output[c].set(out);
    return true;
  }
}
registerProcessor("konkord-dpdfnet", KonkordDpdfnet);
`;
